import { t } from "i18next";

import { MEDIA_CATEGORIES } from "@/src/pages/consumer/components/AuctionWizard/steps/StepMedia/newStepMedia.lib";

import {
  AuctionDTOBase,
  AuctionItemDetailsDTO,
  AuctionItemMediaDTO,
  AuctionState,
  BidState,
  ConsumerAuctionDTO,
  DealerAuctionDTO,
  OfferDTO,
  VehicleDTO,
  VehicleType
} from "../types/types";
import { addThousandsSeparator } from "./numberUtils";

import { parseISO } from "date-fns";

const timeSortFunction = (
  aValue: number,
  bValue: number,
  direction: "asc" | "desc"
) => {
  if (aValue < 0 && bValue < 0) {
    return 0;
  } else if (aValue > 0 && bValue < 0) {
    return -1;
  } else if (aValue < 0 && bValue > 0) {
    return 1;
  } else if (aValue < bValue) {
    return direction === "asc" ? -1 : 1;
  } else if (aValue > bValue) {
    return direction === "asc" ? 1 : -1;
  } else {
    return 0;
  }
};

export const sortOffers = (offers: OfferDTO[] | undefined) => {
  return offers
    ?.sort((a, b) => b.amount - a.amount)
    .sort((a, b) => {
      if (a.state === BidState.DECLINED) return 1;
      if (b.state === BidState.DECLINED) return -1;
      return 0;
    });
};

export const auctionItemEndTimeAscendingSort = (
  a: AuctionDTOBase | undefined,
  b: AuctionDTOBase | undefined
) => {
  const aEndTime = a && a.endTime ? parseISO(a.endTime).getTime() : -1;
  const bEndTime = b && b.endTime ? parseISO(b.endTime).getTime() : -1;
  return timeSortFunction(aEndTime, bEndTime, "asc");
};

export const auctionItemEndTimeDescendingSort = (
  a: AuctionDTOBase | undefined,
  b: AuctionDTOBase | undefined
) => {
  const aEndTime = a && a.endTime ? parseISO(a.endTime).getTime() : -1;
  const bEndTime = b && b.endTime ? parseISO(b.endTime).getTime() : -1;
  return timeSortFunction(aEndTime, bEndTime, "desc");
};

export const auctionItemStartTimeAscendingSort = (
  a: AuctionDTOBase | undefined,
  b: AuctionDTOBase | undefined
) => {
  const aStartTime = a && a.startTime ? parseISO(a.startTime).getTime() : -1;
  const bStartTime = b && b.startTime ? parseISO(b.startTime).getTime() : -1;
  return timeSortFunction(aStartTime, bStartTime, "asc");
};

export const auctionItemStartTimeDescendingSort = (
  a: AuctionDTOBase | undefined,
  b: AuctionDTOBase | undefined
) => {
  const aStartTime = a && a.startTime ? parseISO(a.startTime).getTime() : -1;
  const bStartTime = b && b.startTime ? parseISO(b.startTime).getTime() : -1;
  return timeSortFunction(aStartTime, bStartTime, "desc");
};

export const doesAuctionItemMatchWithSearchTerms = (
  auction: AuctionDTOBase,
  searchTerms: string
) => {
  // Clean the search terms
  const rawWords = searchTerms.split(" ");
  const cleanedWords: string[] = [];
  rawWords.forEach((word) => {
    const cleanedWord = word.trim().toLowerCase();
    if (cleanedWord.length > 1) {
      cleanedWords.push(cleanedWord);
    }
  });

  let matchingTerms = 0;

  const vehicle = auction.auctionItem.vehicle;

  if (vehicle) {
    const searchableTexts = `${vehicle.make ? vehicle.make : ""} ${
      vehicle.model ? vehicle.model : ""
    } ${vehicle.modelType ? vehicle.modelType : ""} ${
      vehicle.modelTypeName ? vehicle.modelTypeName : ""
    } ${vehicle.regNo ? vehicle.regNo : ""}`.toLocaleLowerCase();
    cleanedWords.forEach((word) => {
      if (searchableTexts.indexOf(word) >= 0) {
        matchingTerms++;
      }
    });
  }
  return cleanedWords.length === matchingTerms && matchingTerms > 0;
};

// Helper function to check if a value represents a damage image
export const isDamageFrom = (value: string): boolean => {
  const from = value ?? "";
  const damagePrefixes = ["damage_", MEDIA_CATEGORIES.DAMAGE_IMAGES];
  return damagePrefixes.some((prefix) => from.startsWith(prefix));
};

export const sortMedia = (a: AuctionItemMediaDTO, b: AuctionItemMediaDTO) => {
  // Define the order: general images first, then damaged images, then others
  const order = [
    // General images (main photo and other general images)
    MEDIA_CATEGORIES.GENERAL_IMAGES,
    // Damage images
    "damage_",
    // Other specific image types
    "frontAngle",
    "front",
    "backAngle",
    "back",
    "side",
    "dashboard",
    "optional"
  ];

  // Check if both are general images first
  const aIsGeneral = a.from === MEDIA_CATEGORIES.GENERAL_IMAGES;
  const bIsGeneral = b.from === MEDIA_CATEGORIES.GENERAL_IMAGES;

  if (aIsGeneral && !bIsGeneral) return -1;
  if (!aIsGeneral && bIsGeneral) return 1;

  // Check if both are damage images using shared prefix logic
  const aIsDamage = isDamageFrom(a.from);
  const bIsDamage = isDamageFrom(b.from);

  if (aIsDamage && !bIsDamage && !bIsGeneral) return -1;
  if (!aIsDamage && bIsDamage && !aIsGeneral) return 1;

  // For damage images, sort among themselves (both are damage images)
  if (aIsDamage && bIsDamage) return 0;

  // For other types, use the defined order
  const aFrom = a.from ?? "";
  const bFrom = b.from ?? "";
  const indexA = order.findIndex((item) => aFrom.startsWith(item));
  const indexB = order.findIndex((item) => bFrom.startsWith(item));

  if (indexA === -1 && indexB === -1) return 0; // Both are unknown types
  if (indexA === -1) return 1; // a is unknown type
  if (indexB === -1) return -1; // b is unknown type
  return indexA - indexB; // Sort by defined order
};

export const filterActiveAuctions = (
  auctions: ConsumerAuctionDTO[]
): ConsumerAuctionDTO[] => {
  return auctions.filter((auction) => {
    // Include drafts as they are active auctions
    if (auction.auctionState === AuctionState.DRAFT) {
      return true;
    }

    // Check if ENDED auction has pending offer (negotiation)
    const hasPendingOffer =
      auction.auctionState === AuctionState.ENDED &&
      (!!auction.offerEndTime || !!auction.acceptedOffer);

    // Check if ENDED auction has offers with OK state
    const hasOkOffers =
      auction.auctionState === AuctionState.ENDED &&
      auction?.offers?.some((offer) => offer.state === BidState.OK);

    // Active auctions: things user needs to act on
    return (
      hasPendingOffer ||
      hasOkOffers ||
      auction.auctionState === AuctionState.STARTED ||
      auction.auctionState === AuctionState.OFFER_ACCEPTED
    );
  });
};

export const resolveModelType = (auction: AuctionDTOBase): string => {
  const vehicle: VehicleDTO | undefined =
    auction && auction.auctionItem && auction.auctionItem.vehicle;
  let retValue = "";
  if (vehicle) {
    const modelType = (vehicle.modelType ? vehicle.modelType : "").trim();
    const modelTypeName = (
      vehicle.modelTypeName ? vehicle.modelTypeName : ""
    ).trim();
    retValue = modelType !== "" ? modelType : modelTypeName;
  }
  return retValue;
};

export const resolveMakeModel = (auction: AuctionDTOBase): string => {
  const vehicle: VehicleDTO | undefined =
    auction && auction.auctionItem && auction.auctionItem.vehicle;
  let retValue = "";
  if (vehicle) {
    retValue = vehicle.make ? vehicle.make : "";
    if (vehicle.model) {
      retValue = `${retValue} ${vehicle.model}`;
    }
    retValue = retValue.trim();
  }
  return retValue;
};

export const resolveMakeModelModelType = (auction: AuctionDTOBase): string => {
  const vehicle: VehicleDTO | undefined =
    auction && auction.auctionItem && auction.auctionItem.vehicle;
  let retValue = "";
  if (vehicle) {
    retValue = resolveMakeModel(auction);
    const modelType = resolveModelType(auction);
    if (modelType !== "") {
      retValue = `${retValue} ${modelType}`;
    }
    retValue = retValue.trim();
  }
  return retValue;
};

export const resolveMakeModelRegistrationNumber = (
  auction: AuctionDTOBase
): string => {
  let retValue = resolveMakeModel(auction);
  const regNumber = auction.auctionItem.vehicle?.regNo.toUpperCase();
  if (regNumber && retValue !== "") {
    retValue = `${retValue} (${regNumber})`;
  }
  return retValue;
};

export const resolveYearMileage = (auction: AuctionDTOBase): string => {
  const vehicle: VehicleDTO | undefined =
    auction && auction.auctionItem && auction.auctionItem.vehicle;
  const auctionItemDetails: AuctionItemDetailsDTO | undefined =
    auction && auction.auctionItem && auction.auctionItem.auctionItemDetails;
  const mileageUnit = auction.auctionItem.auctionItemDetails?.mileageUnit
    ? t(
        `dealer.auctionDetailsPage.mileageUnit.${auction.auctionItem.auctionItemDetails.mileageUnit}`
      )
    : "km";
  let retValue = "";
  if (vehicle?.modelYear && vehicle.modelYear > 0) {
    retValue = `${vehicle.modelYear}`;
  }
  const mileage = auctionItemDetails?.mileage;
  if (mileage && mileage > 0) {
    const mileageString = `${addThousandsSeparator(
      Math.ceil(mileage)
    )} ${mileageUnit}`;
    retValue =
      retValue !== "" ? `${retValue} | ${mileageString}` : mileageString;
  }
  return retValue;
};

export const resolveYear = (auction: AuctionDTOBase): string => {
  const vehicle: VehicleDTO | undefined =
    auction && auction.auctionItem && auction.auctionItem.vehicle;
  let retValue = "";
  if (vehicle?.modelYear && vehicle.modelYear > 0) {
    retValue = `${vehicle.modelYear}`;
  }
  return retValue;
};
export const resolveMileage = (auction: AuctionDTOBase): string => {
  const auctionItemDetails: AuctionItemDetailsDTO | undefined =
    auction && auction.auctionItem && auction.auctionItem.auctionItemDetails;
  const mileageUnit = auction.auctionItem.auctionItemDetails?.mileageUnit
    ? t(
        `dealer.auctionDetailsPage.mileageUnit.${auction.auctionItem.auctionItemDetails.mileageUnit}`
      )
    : " km";

  let retValue = "";
  const mileage = auctionItemDetails?.mileage;
  if (mileage && mileage > 0) {
    const mileageString = `${addThousandsSeparator(
      Math.ceil(mileage)
    )}${mileageUnit}`;
    retValue =
      retValue !== "" ? `${retValue} / ${mileageString}` : mileageString;
  }
  return retValue;
};

export const resolveBatteryCapacity = (auction: AuctionDTOBase): string => {
  const vehicle: VehicleDTO | undefined =
    auction && auction.auctionItem && auction.auctionItem.vehicle;
  let retValue = "";
  if (vehicle?.batteryCapacity && vehicle.batteryCapacity > 0) {
    retValue = `${vehicle.batteryCapacity} kWh`;
  }
  return retValue;
};

export const resolveEngineSize = (auction: AuctionDTOBase): string => {
  const rawEngineSize: unknown = auction?.auctionItem?.vehicle?.engineSize;
  const vehicleType = auction?.auctionItem?.vehicle?.vehicleType;

  if (!rawEngineSize) {
    return "";
  }

  let engineSize: number;
  if (typeof rawEngineSize === "string") {
    engineSize = Number.parseFloat(rawEngineSize.replace(",", "."));
  } else if (typeof rawEngineSize === "number") {
    engineSize = rawEngineSize;
  } else {
    return "";
  }

  if (isNaN(engineSize) || engineSize <= 0) {
    return "";
  }

  if (vehicleType === VehicleType.MOTO) {
    const engineUnit = "cm³";
    return `${addThousandsSeparator(engineSize)} ${engineUnit}`;
  } else {
    const engineUnit = "l";
    return `${engineSize.toFixed(1)} ${engineUnit}`;
  }
};

export const resolveLocationDistance = (auction: DealerAuctionDTO): string => {
  let retValue = "";
  const city = auction["city"];
  if (city && city !== "") {
    retValue = city;
  }
  const distance = auction.distanceBetween;
  if (distance && distance >= 0) {
    const distanceString = `${addThousandsSeparator(Math.ceil(distance))} km`;
    retValue =
      retValue !== "" ? `${retValue} | ${distanceString}` : distanceString;
  }
  return retValue;
};

export const resolveKeySpecs = (auction: DealerAuctionDTO): string => {
  const vehicle: VehicleDTO | undefined =
    auction && auction.auctionItem && auction.auctionItem.vehicle;
  const specs = [
    vehicle?.fuelType,
    resolveEngineSize(auction),
    resolveBatteryCapacity(auction),
    vehicle?.gearType,
    vehicle?.driveType
  ];
  return specs.filter(Boolean).join(" | ");
};

export const calculateRemainingTimeInSeconds = (
  timestamp: string,
  currentServerTime: number
) => {
  // Calculate the remaining time in seconds
  let timeLeft = 0;
  if (timestamp) {
    const endDate = parseISO(timestamp);
    const currentDate = new Date(currentServerTime);
    const remainingMilliSeconds = endDate.getTime() - currentDate.getTime();
    timeLeft = Math.floor(remainingMilliSeconds / 1000);
    if (timeLeft < 0) {
      timeLeft = 0;
    }
  }
  return timeLeft;
};

export type AuctionStatePresentation = {
  state:
    | "STARTED"
    | "REJECTED_BY_DEALER"
    | "REJECTED_BY_SELLER"
    | "UNDER_NEGOTIATION"
    | "OFFER_ACCEPTED"
    | "SOLD"
    | "ENDED";
  severity: "error" | "warning" | "info" | "success";
  color: "error" | "warning" | "info" | "success" | "secondary";
  text: string;
};

export const resolveAuctionStatePresentation = (
  auction: DealerAuctionDTO
): AuctionStatePresentation | null => {
  if (auction.auctionState === AuctionState.STARTED) {
    return {
      state: "STARTED",
      severity: "info",
      color: "info",
      text: t("auctionStates.buyer.STARTED")
    };
  }
  const bid = auction.myHighestBid || auction.ourHighestBid;
  if (bid) {
    if (
      (auction.auctionState === AuctionState.SOLD ||
        auction.auctionState === AuctionState.BILLED ||
        auction.auctionState === AuctionState.SENT_FOR_BILLING) &&
      bid.state === BidState.ACCEPTED
    ) {
      return {
        state: "SOLD",
        severity: "success",
        color: "success",
        text: t("auctionStates.buyer.SOLD")
      };
    } else if (
      bid.state === BidState.IN_NEGOTIATION ||
      auction.ourHighestBid?.state === BidState.IN_NEGOTIATION
    ) {
      return {
        state: "UNDER_NEGOTIATION",
        severity: "info",
        color: "warning",
        text: t("auctionStates.buyer.UNDER_NEGOTIATION")
      };
    } else if (
      bid.state === BidState.WITHDRAWN ||
      auction.auctionState === AuctionState.REJECTED_BY_DEALER
    ) {
      return {
        state: "REJECTED_BY_DEALER",
        severity: "error",
        color: "error",
        text: t("auctionStates.buyer.REJECTED_BY_DEALER")
      };
    } else if (
      auction.auctionState === AuctionState.NOT_SOLD ||
      bid.state === BidState.DECLINED
    ) {
      return {
        state: "REJECTED_BY_SELLER",
        severity: "error",
        color: "error",
        text: t("auctionStates.buyer.REJECTED_BY_SELLER")
      };
    } else if (bid.state === BidState.ACCEPTED) {
      return {
        state: "OFFER_ACCEPTED",
        severity: "success",
        color: "success",
        text: t("auctionStates.buyer.OFFER_ACCEPTED")
      };
    } else {
      return {
        state: "ENDED",
        severity: "info",
        color: "info",
        text: t("auctionStates.buyer.ENDED")
      };
    }
  }
  return null;
};

export const isBidMachineActive = (auction?: DealerAuctionDTO | null) => {
  let isActive = false;
  if (auction) {
    const myBidMachine = auction.myBidMachine;
    const highestBid = auction.highestBid;
    isActive =
      myBidMachine !== undefined &&
      highestBid !== undefined &&
      myBidMachine.maxAmount > 0 &&
      myBidMachine.maxAmount > highestBid.amount;
  }
  return isActive;
};
